import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPix } from '@fortawesome/free-brands-svg-icons';
import { faCheckSquare, faClock, faEye, faEyeSlash, faWallet, faWindowClose, faBarcode, faLandmark, faChevronLeft, faChevronRight, faMoneyBillTransfer } from '@fortawesome/free-solid-svg-icons';
import { UserContext } from '../../context/UserContext';
import WalletService from '../../../services/WalletService';
import Conversor from '../../../helper/Conversor';
import Swal from 'sweetalert2';
import Inputmask from 'inputmask';
import WalletFilters from './components/WalletFilters';
import { Oval } from 'react-loader-spinner';
import './assets/scss/main.scss';
import ReportsService from '../../../services/ReportsService';

const Wallet = (props) => {

  window.document.title = `Carteira | Motum`;

  const [hideBalance, setHideBalance] = useState(false);
  const [loading, setLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [spinnerTransactions, setSpinnerTransactions] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const actualDate = new Intl.DateTimeFormat('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date()).split('/').reverse().join('-');

  const [filters, setFilters] = useState({
    startDate: actualDate,
    endDate: actualDate,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalIntegrations: 0,
    totalPages: 1,
  });

  const previousPage = () => {
    setPagination((prevFilters) => ({
      ...prevFilters,
      currentPage: pagination.currentPage - 1
    }));
    setLoadingTransactions(false);
  }

  const nextPage = () => {
    setPagination((prevFilters) => ({
      ...prevFilters,
      currentPage: pagination.currentPage + 1
    }));
    setLoadingTransactions(false);
  }

  const { user, selectedStore, stores } = useContext(UserContext);
  const walletService = useMemo(() => new WalletService(), []);
  const reportsService = useMemo(() => new ReportsService(), []);

  async function loadWallet() {
    if (loading) return;
    setLoading(true);
    props.setSpinner(true);
    setTimeout(async () => {
      try {
          const response = await walletService.loadWallet(user._id);
          setWallet(response.wallet);
      } catch (error) {
          console.error("Error loading wallet:", error);
      } finally {
          props.setSpinner(false);
      }
    }, 2000);
  }

  async function loadTransactions() {
    if (loadingTransactions) return;
    setLoadingTransactions(true);
    setSpinnerTransactions(true);
    const response = await walletService.loadTransactions(wallet._id, filters, pagination.currentPage);
    setTransactions(response.data);
    setPagination((prevFilters) => ({
      ...prevFilters,
      totalIntegrations: response.pagination.totalTransactions,
      totalPages: response.pagination.totalPages
    }));
    setTimeout(async () => {
      setSpinnerTransactions(false)
      setLoadingTransactions(false);
    }, 1000);
  }

  const exportTransactions = async () => {
    try {
      props.setSpinner(true);
      const { data } = await reportsService.exportWalletTransactions(filters.startDate, filters.endDate, user._id);
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transactions_${user._id}-${filters.startDate}-${filters.endDate}.xlsx`); // Nome do arquivo
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
        console.error('Erro ao baixar o arquivo:', error);
        alert('Erro ao gerar o relatório.');
    } finally {
        props.setSpinner(false);
    }
  };

  function priceToBRLCurrency(valor = 0) {
    return Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);
  }

  async function copyTextToClipboard(text) {
    if ("clipboard" in navigator) {
      await navigator.clipboard.writeText(text);
    } else {
      document.execCommand("copy", true, text);
    }
    Swal.update({
      confirmButtonText: 'Chave copiada!',
      confirmButtonColor: '#28a745'
    });
    setTimeout(() => {
      Swal.update({
        confirmButtonText: 'Copiar QR Code',
        confirmButtonColor: '#000',
      });
    }, 3000);
    return;
  }

  const handleTrasnferDebit = async () => {
    const storeOptions = stores.filter(store => store.userId !== user._id).map(store => `<option value="${store._id}">${store.companyName}</option>`).join('');

    const { value: formValues } = await Swal.fire({
      title: 'Informe o valor recebido',
      html: `
        <select id="store-select" class="select-store-transfer">
          <option value="">Selecione a loja</option>
          ${storeOptions}
        </select>
        <input id="transfer-input" type="text" class="swal2-input" placeholder="R$ 0,00" />
      `,
      showCancelButton: true,
      confirmButtonText: 'Transferir débito',
      confirmButtonColor: '#000',
      cancelButtonText: 'Cancelar',
      didOpen: () => {
        const input = document.getElementById('transfer-input');
        Inputmask({
          alias: 'numeric',
          groupSeparator: '.',
          radixPoint: ',',
          prefix: 'R$ ',
          autoGroup: true,
          digits: 2,
          digitsOptional: false,
          placeholder: '0',
          clearMaskOnLostFocus: false
        }).mask(input);
      },
      preConfirm: () => {
        const input = document.getElementById('transfer-input').value;
        const storeId = document.getElementById('store-select').value;
        const numericValue = parseFloat(input.replace('R$ ', '').replace(/\./g, '').replace(',', '.'));

        if (isNaN(numericValue) || numericValue <= 0) {
          Swal.showValidationMessage('Por favor, insira um valor válido!');
          return null;
        }

        if (!storeId) {
          Swal.showValidationMessage('Por favor, selecione uma loja!');
          return null;
        }

        return { amount: numericValue, storeId };
      }
    });

    if (formValues) {
      const result = await Swal.fire({
        title: `Deseja adicionar o débito de ${Conversor.converterStringReal(formValues.amount)} da loja ${stores.find(store => store._id === formValues.storeId).companyName} a sua conta?`,
        text: "Essa ação é irrevesível",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#ccc',
        confirmButtonText: 'Sim, adicionar!',
        cancelButtonText: 'Cancelar',
      });
      if (result.isConfirmed) {
        try {
          const response = await walletService.transferDebit(formValues.storeId, formValues.amount);
          if (response) {
            Swal.fire({
              icon: 'success',
              title: "Débito inserido com sucesso",
              showConfirmButton: false,
              timer: 2000,
              didClose: () => {
                setLoading(false);
              }
            });
          }
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: `Ocorreu um erro ao adicionar o débito`,
            text: error.response.data.error,
            showConfirmButton: false,
            timer: 2000
          });
        }
      }
    };
  }

  const handleAddBalance = async () => {
    Swal.fire({
      icon: "info",
      title: "Operação indisponível",
      text: "Entre em contato com o suporte para mais informações. Whatsapp: (11) 94222-5540",
      showConfirmButton: true,
    });
    // const { value: depositAmount } = await Swal.fire({
    //   title: 'Digite o valor da transferência',
    //   html: `<input id="saldo-input" type="text" class="swal2-input" placeholder="R$ 0,00" />`,
    //   showCancelButton: true,
    //   confirmButtonText: 'Gerar Pix',
    //   confirmButtonColor: '#000',
    //   cancelButtonText: 'Cancelar',
    //   didOpen: () => {
    //     const input = document.getElementById('saldo-input');
    //     Inputmask({
    //       alias: 'numeric',
    //       groupSeparator: '.',
    //       radixPoint: ',',
    //       prefix: 'R$ ',
    //       autoGroup: true,
    //       digits: 2,
    //       digitsOptional: false,
    //       placeholder: '0',
    //       clearMaskOnLostFocus: false
    //     }).mask(input);
    //   },
    //   preConfirm: () => {
    //     const input = document.getElementById('saldo-input').value;
    //     const numericValue = parseFloat(input.replace('R$ ', '').replace(/\./g, '').replace(',', '.'));

    //     if (isNaN(numericValue) || numericValue <= 0) {
    //       Swal.showValidationMessage('Por favor, insira um valor válido!');
    //       return null;
    //     }

    //     return numericValue;
    //   }
    // });

    // if (depositAmount) {
    //   try {
    //     props.setSpinner(true);
    //     const response = await walletService.newIntentDeposit(selectedStore._id, depositAmount);
    //     const pixData = response.data;
    //     props.setSpinner(false);
    //     Swal.fire({
    //       title: 'Use o QR Code do Pix abaixo',
    //       html: `
    //         <div class="pix-modal">
    //           <p class="pix-modal-value">Valor: ${priceToBRLCurrency(depositAmount)}</p>
    //           <p class="pix-modal-id">ID da transação<br> ${pixData.transactionId}</p>
    //           <div style="margin: 20px 0;" class="pix-modal-qr-code">
    //             <img src="data:image/png;base64,${pixData.qrCode}" alt="QR Code para pagamento" style="width: 200px; height: 200px; border: 1px solid #ccc; border-radius: 8px;" />
    //           </div>
    //           <p class="pix-modal-key-title">Pix Copia e Cola</p>
    //           <input class="pix-modal-key-input" type="text" value="${pixData.code}" readonly style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px;" />
    //         </div>
    //       `,
    //       showCloseButton: true,
    //       showCancelButton: true,
    //       cancelButtonText: 'Fechar',
    //       confirmButtonText: 'Copiar QR Code',
    //       confirmButtonColor: '#000',
    //       allowOutsideClick: false,
    //       preConfirm: async () => {
    //         await copyTextToClipboard(pixData.code);
    //         return false;
    //       }
    //     });
    //   } catch (error) {
    //     props.setSpinner(false);
    //     Swal.fire({
    //       icon: 'error',
    //       title: 'Ocorreu um erro!',
    //       text: 'Tente novamente mais tarde.',
    //       showConfirmButton: false,
    //       timer: 2000
    //     });
    //   }
    // }
  };

  useEffect(() => {
    loadWallet();
    // eslint-disable-next-line
  }, [loading]);

  useEffect(() => {
    if (wallet) loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet, filters, pagination]);

  const transactionMap = {
    "CREDIT": "CRÉDITO",
    "DEBIT": "DÉBITO",
    "PENDING": "PENDENTE",
    "APPROVED": "APROVADO",
    "CANCELLED": "CANCELADO"
  }

  const transactionIconMap = {
    "APPROVED": <FontAwesomeIcon icon={faCheckSquare} color="#168821ff" style={{ marginRight: 10 }} />,
    "PENDING": <FontAwesomeIcon icon={faClock} color="#ffcd07ff" style={{ marginRight: 10 }} />,
    "CANCELLED": <FontAwesomeIcon icon={faWindowClose} color="#e52207ff" style={{ marginRight: 10 }} />,
  };

  return (
    <React.Fragment>
      <div className="wallet-container">
        <div className="main-balance-container">
          <div className="balance-info-container">
            <div className="balance-info-header">
              <span>Conta</span>
              <FontAwesomeIcon icon={faWallet} />
            </div>
            <div className="balance-info-amount-container">
              <div className="balance-info-amount">
                <div className="amount-title">Saldo disponível</div>
                <div className="amount-value">{!hideBalance && wallet ? Conversor.converterStringReal(wallet.amount.value) : "***********"}</div>
              </div>
              <div className="hide-amount" onClick={() => setHideBalance(prevState => !prevState)}><FontAwesomeIcon icon={!hideBalance ? faEyeSlash : faEye} /></div>
            </div>
          </div>

          <div className="balance-actions-container">
            <div className="action-item" onClick={handleAddBalance} >
              <div className="action-icon"><FontAwesomeIcon icon={faPix} /></div>
              <div className="action-text">Depositar com Pix</div>
            </div>
            {(user.type === "HUB" || user.type === "LICENSED") &&
              <div className="action-item" onClick={handleTrasnferDebit}>
                <div className="action-icon"><FontAwesomeIcon icon={faMoneyBillTransfer} /></div>
                <div className="action-text">Receber Débito</div>
              </div>
            }
            <div className="action-item" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
              <div className="action-icon"><FontAwesomeIcon icon={faPix} /></div>
              <div className="action-text">Sacar com Pix</div>
            </div>
            <div className="action-item" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
              <div className="action-icon"><FontAwesomeIcon icon={faBarcode} /></div>
              <div className="action-text">Boleto</div>
            </div>
            <div className="action-item" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
              <div className="action-icon"><FontAwesomeIcon icon={faLandmark} /></div>
              <div className="action-text">Extrato</div>
            </div>
          </div>
        </div>
        <div className="transactions-container">
          <div className="transactions-title">Histórico de transações</div>
          <WalletFilters filters={filters} setFilters={setFilters} exportTransactions={exportTransactions} />
          <div className="transactions-table">
            {spinnerTransactions ? (
              <div className="loading-transactions">
                <Oval
                  visible={loadingTransactions}
                  height="50"
                  width="50"
                  color="#000"
                  secondaryColor="#ccc"
                  radius="1"
                />
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th className="transaction-date">Data da transação</th>
                    <th className="transaction-value">Valor</th>
                    <th className="transaction-description">Descrição</th>
                    <th className="transaction-status">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => (
                    <tr key={transaction._id}>
                      <td className="transaction-date">{Conversor.converterData(transaction.movementAt)}</td>
                      <td className={`transaction-value${transaction.type === "CREDIT" ? "-credit" : "-debit"}`}>{transaction.type === "CREDIT" ? "+ " : "- "}{Conversor.converterStringReal(transaction.amount.value)}</td>
                      <td className="transaction-description">{transaction.description ? transaction.description : "Não informado"}</td>
                      <td className="transaction-status">{transactionIconMap[transaction.status]}{transactionMap[transaction.status]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="pagination-container">
              {pagination.currentPage > 1 && <button type="button" onClick={previousPage}><FontAwesomeIcon icon={faChevronLeft} /></button>}
              {pagination.totalPages > 1 && <span>{pagination.currentPage}</span>}
              {pagination.currentPage < pagination.totalPages && <button type="button" onClick={nextPage}><FontAwesomeIcon icon={faChevronRight} /></button>}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Wallet;
